import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { ProfileService } from "../services/profile";
import { ProfileData, ProfileFormData, UsageSummary } from "../types";

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: "",
    email: "",
    avatar_url: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [isLoadingUsageSummary, setIsLoadingUsageSummary] = useState(false);
  const [usageSummaryError, setUsageSummaryError] = useState<string | null>(null);

  const profileFetchAttemptedForUserId = useRef<string | null>(null);

  const fetchProfileData = useCallback(async () => {
    if (!user?.id) return;

    setIsLoadingProfile(true);
    setError(null);
    
    const { data, error } = await ProfileService.fetchProfile(user.id);
    
    if (error) {
      setError(error);
    } else if (data) {
      setProfile(data);
      setFormData({
        name: data.name || "",
        email: data.email || "",
        avatar_url: data.avatar_url || "",
      });
    }
    
    setIsLoadingProfile(false);
  }, [user?.id]);

  const saveProfile = async () => {
    if (!user?.id) return;

    setIsSaving(true);
    setError(null);
    
    const { success, error } = await ProfileService.updateProfile(user.id, formData);
    
    if (error) {
      setError(error);
    } else {
      // Fetch updated profile data to reflect changes
      await fetchProfileData();
      setIsEditing(false); // Exit edit mode on successful save
    }
    
    setIsSaving(false);
  };

  const fetchUsageSummary = useCallback(async () => {
    if (!user?.id) return;

    setIsLoadingUsageSummary(true);
    setUsageSummaryError(null);

    const { data, error: usageError } = await ProfileService.fetchUsageSummary();

    if (usageError) {
      setUsageSummary(null);
      setUsageSummaryError(usageError);
    } else {
      setUsageSummary(data);
    }

    setIsLoadingUsageSummary(false);
  }, [user?.id]);

  // Fetch profile when user changes
  useEffect(() => {
    if (user && profileFetchAttemptedForUserId.current !== user.id) {
      profileFetchAttemptedForUserId.current = user.id;
      fetchProfileData();
      fetchUsageSummary();
    } else if (!user) {
      profileFetchAttemptedForUserId.current = null;
      setUsageSummary(null);
    }
  }, [user, fetchProfileData, fetchUsageSummary]);

  return {
    profile,
    isLoadingProfile,
    error,
    isEditing,
    setIsEditing,
    formData,
    setFormData,
    isSaving,
    saveProfile,
    fetchProfileData,
    setError,
    usageSummary,
    isLoadingUsageSummary,
    usageSummaryError,
    fetchUsageSummary,
  };
} 